import React, { useMemo, useState, useEffect } from "react";
import submit from "submit-form";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { DragDropProvider, DragOverlay } from "@dnd-kit/react";
import SvgIcon from "../../../../../parts/SvgIcon";
import { MealPlannerCalendar } from "./MealPlannerCalendar";
import { MealPlannerDragOverlayContent } from "../shared/MealPlannerDragOverlayContent";
import {
  completeTemplateWeek,
  completeWeek,
  getDateWithDayToDisplay,
} from "../../../../../utils/date-helpers";
import {
  formatNutritionValue,
  CALORIES_PER_GRAM,
} from "../../../../../utils/nutrition-util";
import ToolTip from "../../../../../parts/tooltip";
import { createMealDropHandler } from "../utils/meal-drag-drop-helpers";

export const MealPlannerWeekResume = ({ mealPlan, locals, api }) => {
  const { t } = useTranslation("athlete-portal");
  const generationStatusMap = {
    success: {
      color: "bg-(--success)",
      tooltip: t("mealPlannerWeekResume.generationStatusSuccess"),
    },
    warning: {
      color: "bg-(--warning)",
      tooltip: t("mealPlannerWeekResume.generationStatusWarning"),
    },
    failed: {
      color: "bg-(--danger)",
      tooltip: t("mealPlannerWeekResume.generationStatusFailed"),
    },
  };
  const { query, nutritionPlans, activeExercises, basicMealsInformation } =
    locals;

  // Local state for optimistic updates
  const [localMealPlan, setLocalMealPlan] = useState(mealPlan);

  // Sync local state when prop changes (e.g., after API refresh)
  useEffect(() => {
    setLocalMealPlan(mealPlan);
  }, [mealPlan]);

  const { weeks = [] } = localMealPlan;
  const {
    week = 1,
    plan = "",
    athlete,
    mealPlan: mealPlanId,
    viewMode,
  } = query;
  const selectedWeekContent = weeks[week - 1];
  const selectedWeek = selectedWeekContent?.days || [];
  const mealTypes = selectedWeek[0]?.meals
    .sort((a, b) => a.order - b.order)
    .map(({ type }) => type);

  const [openedItem, setOpenedItem] = useState(null); // Track the currently opened item
  const basicMealTypes = ["Breakfast", "Lunch", "Snack", "Dinner"];
  const selectedNutritionPlan = nutritionPlans
    ? nutritionPlans.find((n) => n._id === plan)
    : null;
  const isFromNutritionPlan = athlete && selectedNutritionPlan;

  const macroInfo = [
    { label: t("mealPlannerOption.proteins"), key: "proteins", className: "protein" },
    { label: t("mealPlannerOption.carbs"), key: "carbs", className: "carbs" },
    { label: t("mealPlannerOption.fats"), key: "fats", className: "fat" },
    { label: t("mealPlannerOption.fibers"), key: "fibers", className: "calories-text" },
    { label: t("mealPlannerOption.calories"), key: "calories", className: "calories-text" },
  ];
  const days = useMemo(() => {
    if (selectedNutritionPlan) {
      return completeWeek(selectedWeek, activeExercises, selectedNutritionPlan);
    }
    return completeTemplateWeek(selectedWeek, mealPlan);
  }, [selectedWeek, activeExercises, selectedNutritionPlan, mealPlan]);
  //get the meals for each date
  const mealsByDate = useMemo(() => {
    return selectedWeek.reduce((acc, { date, meals }, index) => {
      const key = date ?? t("mealPlannerWeekResume.dayLabel", { index: index + 1 });
      acc[key] = meals;
      return acc;
    }, {});
  }, [selectedWeek, t]);

  /**
   * Function to open the modals
   * @param {*} modal
   * @param {*} date
   */
  const handleOpenModal = (modal, date, extra = {}) => {
    submit(`/actions/refresh/?`, {
      method: "GET",
      body: { "modal[type]": modal, ...(date && { date }), ...extra },
    });
  };

  /**
   * Function to delete a custom meal
   * @param {*} e
   */
  const handleDeleteCustomMeal = (e, meal) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      process.browser &&
      confirm(t("mealPlannerSimplifiedCalendar.deleteCustomMealConfirm"))
    ) {
      const body = {
        meal,
        bodyAction: "handle-custom-meal",
        handle: "delete",
      };

      submit(
        `/actions/meal-planner/update-days-meal-plan?mealPlanId=${mealPlanId}&proxyAthlete=${athlete}`,
        {
          method: "POST",
          body,
        },
      );
    }
  };

  /**
   * Function to remove all the foods of a day
   * @param {*} e
   * @param {*} date
   */
  const removeAllFoods = (e, date) => {
    e.preventDefault();
    e.stopPropagation();
    if (
      process.browser &&
      confirm(t("mealPlannerWeekResume.deleteDayContentConfirm"))
    ) {
      const weekIndex = week - 1;
      const body = {
        weekIndex,
        date,
        bodyAction: "handle-food",
        handle: "delete",
        isTemplate: isFromNutritionPlan ? false : true,
      };

      submit(
        `/actions/meal-planner/update-days-meal-plan?mealPlanId=${mealPlanId}&proxyAthlete=${athlete}`,
        {
          method: "POST",
          body,
        },
      );
    }
  };

  const getDayGenerationStatus = (dayIndex) => {
    const day = selectedWeek[dayIndex];
    if (!day) return { generationStatus: null, regenerationStatus: null };

    return {
      generationStatus: day.generation?.status || null,
      regenerationStatus: day.regeneration?.status || null,
    };
  };

  const handleManualRegeneration = (dayIndex) => {
    if (
      process.browser &&
      confirm(t("mealPlannerWeekResume.regenerateDayConfirm"))
    ) {
      const body = {
        mealPlanId: mealPlanId,
        weekIndex: week - 1,
        dayIndex: dayIndex,
      };

      submit(`/actions/meal-planner/regenerate-day-meal-plan`, {
        method: "POST",
        body,
      });
    }
  };

  // State for drag overlay
  const [activeId, setActiveId] = useState(null);
  const [activeData, setActiveData] = useState(null);

  // Create drag and drop handler with optimistic update support
  const handleDrop = createMealDropHandler(
    api,
    mealPlanId,
    athlete,
    isFromNutritionPlan,
    {
      // onOptimisticUpdate: Update local state immediately
      onOptimisticUpdate: (updatedMealPlan) => {
        setLocalMealPlan(updatedMealPlan);
      },
      // onRevert: Revert to original state if API fails
      onRevert: (originalMealPlan) => {
        setLocalMealPlan(originalMealPlan);
      },
      // currentMealPlan: Pass current state for optimistic update
      currentMealPlan: localMealPlan,
      type: "move", // Default operation type
      flash: locals.flash,
      submit: submit,
    },
  );

  // Handle drag start for overlay - for individual foods and customMeals (not full meals)
  const handleDragStart = (event) => {
    // DragDropProvider passes operation.source in the event
    const source = event.operation?.source;
    if (!source) return;

    // Try different ways to access data
    const data = source.data?.current || source.data;

    // Track for individual foods (data.type === "food" or "verified-food") and customMeals (data.type === "customMeal")
    // Meals completos NO tienen "type" en su data, solo tienen planMode y mealType
    if (data && (data.type === "food" || data.type === "verified-food" || data.type === "customMeal")) {
      setActiveId(source.id);
      setActiveData(data);
    } else {
      // Clear overlay for full meals (they work fine without overlay)
      setActiveId(null);
      setActiveData(null);
    }
  };

  // Handle drag end - call drop handler and clear overlay
  const handleDragEnd = (event) => {
    // Clear overlay first (so it disappears immediately)
    setActiveId(null);
    setActiveData(null);

    // Execute drop handler immediately - no delay needed with DragOverlay
    handleDrop(event);
  };

  // Handle drag cancel
  const handleDragCancel = () => {
    setActiveId(null);
    setActiveData(null);
  };

  return (
    <div className="resume">
      <div className="resume-nutrition">
        {days.map(({ date, goals, hasData, macrosTotal, hasFoods }, index) => {
          const isOpened = openedItem === index; // Check if the current item is opened
          const { generationStatus, regenerationStatus } =
            getDayGenerationStatus(index);
          const isRegenerating =
            regenerationStatus === "pending" ||
            regenerationStatus === "executing";
          const showRegenerateButton =
            (generationStatus === "warning" || generationStatus === "failed") &&
            !isRegenerating;

          const { color: statusColor, tooltip: statusTooltip } =
            generationStatusMap[generationStatus] || {};

          return (
            <div key={`${date}-${index}`} className="resume-container">
              {/* header*/}
              <div className="resume-stats">
                <div
                  className={
                    hasData
                      ? `header-container bg-(--dark-grey)`
                      : `header-container bg-(--footer-background-color)`
                  }
                >
                  <div className="header-text flex items-center gap-2">
                    {statusColor && (
                      <ToolTip id="status-hint" body={statusTooltip}>
                        <div className={`h-6 w-1 ${statusColor} rounded-sm`} />
                      </ToolTip>
                    )}
                    {isRegenerating && (
                      <div className="status-message">
                        <div className="spinner-div">
                          <img
                            src="/img/spinner.gif"
                            width="32"
                            height="32"
                            alt="spinner"
                          />
                        </div>
                      </div>
                    )}
                    <span>{getDateWithDayToDisplay(date)}</span>
                  </div>
                  {hasData && !viewMode && (
                    <div className="actions">
                      <ToolTip
                        id={`copy-from-template-${date}`}
                        body={t("mealPlannerOption.importSingleDayTemplate")}
                        float={false}
                      >
                        <a
                          className="copy"
                          onClick={() =>
                            handleOpenModal(
                              "replace-day-from-template",
                              date,
                              { hasFoods }
                            )
                          }
                        >
                          <SvgIcon src="/img/new-icons/import-in.svg" />
                        </a>
                      </ToolTip>
                      <details
                        role="list"
                        open={isOpened && hasFoods}
                        className={hasFoods ? "enabled" : "disabled"}
                      >
                        <summary
                          onClick={(e) => {
                            e.preventDefault();
                            if (!hasFoods) {
                              return;
                            }
                            setOpenedItem(isOpened ? null : index);
                          }}
                          onBlur={(e) => {
                            e.preventDefault();
                            setOpenedItem(null);
                          }}
                          aria-haspopup="listbox"
                          className="p-0! h-auto!"
                        >
                          <SvgIcon src="/img/new-icons/vuesax-linear-add-square.svg" />
                        </summary>
                        {hasFoods && (
                          <ul
                            role="listbox"
                            className={isOpened ? "opened" : ""}
                          >
                            <li>
                              <a
                                onMouseDown={() => {
                                  handleOpenModal("move-meal-plan", date);
                                }}
                              >
                                {t("mealPlannerOption.move")}
                              </a>
                            </li>
                            <li>
                              <a
                                onMouseDown={() => {
                                  handleOpenModal("duplicate-meal-plan", date);
                                }}
                              >
                                {t("mealPlannerOption.duplicate")}
                              </a>
                            </li>
                            {showRegenerateButton && (
                              <li>
                                <a
                                  onMouseDown={() =>
                                    handleManualRegeneration(index)
                                  }
                                >
                                  {t("mealPlannerWeekResume.regenerate")}
                                </a>
                              </li>
                            )}
                            <li>
                              <a
                                className="text-(--danger)!"
                                onMouseDown={(e) => removeAllFoods(e, date)}
                              >
                                {t("mealPlannerOption.clear")}
                              </a>
                            </li>
                          </ul>
                        )}
                      </details>
                    </div>
                  )}
                </div>
                {/* content */}
                <div
                  className={`content-container ${
                    hasData
                      ? "bg-(--dark-grey)"
                      : "bg-(--footer-background-color)"
                  } ${!isFromNutritionPlan ? "h-44.25!" : ""}`}
                >
                  {macroInfo.map(({ label, key, className }) => {
                    const actualToUse =
                      macrosTotal?.[key] ??
                      macrosTotal?.macrosTotal?.[key] ??
                      0;

                    const actual = formatNutritionValue(actualToUse);
                    // Get the calories per gram
                    const caloriesPerGram = CALORIES_PER_GRAM[key] || 0;
                    // Calories generated by that macro
                    const macroCalories = actualToUse * caloriesPerGram;
                    const totalCalories =
                      macrosTotal?.macrosTotal?.calories || 0; // Percentage of the total
                    const percentage =
                      key !== "calories" &&
                      caloriesPerGram > 0 &&
                      totalCalories > 0
                        ? ((macroCalories / totalCalories) * 100).toFixed(0)
                        : "0";
                    const goal = formatNutritionValue(goals[key] || 0);
                    const hasTarget = (goals[key] || 0) > 0;
                    // const diff = (macrosTotal[key] || 0) - (goals[key] || 0);
                    const diff = Math.round(actual - goal, 0);
                    const absDiff = formatNutritionValue(Math.abs(diff));
                    const hasDifference = hasTarget && Math.abs(diff) > 0;
                    const statusText = hasDifference
                      ? `${Math.abs(diff)} ${diff < 0 ? t("mealPlannerOption.left") : t("mealPlannerOption.excess")}`
                      : "";

                    return (
                      <div className="info" key={label}>
                        {hasData && (
                          <>
                            <div className="flex flex-col gap-0.5 my-1">
                              <span className={className}>{label}</span>
                              {/* {isFromNutritionPlan && hasTarget && (
                                <span className="opacity-50">
                                  ({diff < 0 ? "Under" : "Over"})
                                </span>
                              )} */}
                              {hasDifference && (
                                <span className="opacity-50">
                                  ({diff < 0 ? t("mealPlannerOption.under") : t("mealPlannerOption.over")})
                                </span>
                              )}
                            </div>
                            <div className="goals my-1">
                              <span>
                                {actual}
                                {hasTarget && `/${goal}`}
                                {key === "calories" ? " kcal" : "g"}
                                {percentage > 0 && (
                                  <span className="text-[0.75rem] opacity-70">
                                    {" "}
                                    ({percentage}%)
                                  </span>
                                )}
                              </span>
                              {hasDifference && (
                                <span className="opacity-50">
                                  ({statusText})
                                </span>
                              )}
                              {/* {isFromNutritionPlan && hasTarget && (
                                <span className="opacity-50">
                                  ({absDiff} {status})
                                </span>
                              )} */}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/*(DETAILED) drag is go and drop is receive */}
      <DragDropProvider
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {/* DragOverlay - render when dragging food or customMeal */}
        {activeId &&
          activeData &&
          (activeData.type === "food" || activeData.type === "verified-food" || activeData.type === "customMeal") && (
            <DragOverlay dropAnimation={null} style={{ pointerEvents: "none" }}>
              <MealPlannerDragOverlayContent
                activeId={activeId}
                activeData={activeData}
              />
            </DragOverlay>
          )}
        <div
          className={
            isFromNutritionPlan
              ? "main-meal-planner-calendar"
              : "mt-0! main-meal-planner-calendar"
          }
        >
          {mealTypes.map((meal, mealIndex) => (
            <div key={`${meal}-${mealIndex}`} className="meal-type-container">
              <div
                className={
                  !basicMealTypes.includes(meal)
                    ? "meal-type-header justify-around"
                    : "meal-type-header justify-center"
                }
              >
                {!basicMealTypes.includes(meal) && !viewMode && (
                  <div className="custom-meal-actions">
                    <a
                      onClick={(e) => {
                        handleDeleteCustomMeal(e, meal);
                      }}
                    >
                      <SvgIcon
                        src="/img/new-icons/red-trash.svg"
                        noFilterOnLight={true}
                      />
                    </a>
                    <a href="/actions/refresh?modal[type]=custom-meal">
                      <SvgIcon src="/img/new-icons/edit-pencil.svg" />
                    </a>
                  </div>
                )}
                <span>{meal}</span>
              </div>
              <div className="meal-type-calendar">
                {days.map((day, dayIndex) => {
                  const mealsForDay = mealsByDate[day.date] || [];
                  const specificMeal =
                    mealsForDay.find((m) => m.type === meal) || {};
                  return (
                    <MealPlannerCalendar
                      query={query}
                      meal={specificMeal}
                      day={day}
                      week={week}
                      viewMode={viewMode}
                      basicMealsInformation={basicMealsInformation}
                      planMode={mealPlan?.planMode || "detailed"}
                      key={`${day?.date}-${dayIndex}`}
                    />
                  );
                })}
              </div>
            </div>
          ))}
          {!viewMode && (
            <div className="add-custom-meal">
              <button
                onClick={() => {
                  handleOpenModal("custom-meal");
                }}
              >
                <SvgIcon src="/img/new-icons/add-square-outline.svg" />
                <span>{t("mealPlannerWeekResume.customMeal")}</span>
              </button>
            </div>
          )}
        </div>
      </DragDropProvider>
    </div>
  );
};

MealPlannerWeekResume.propTypes = {
  mealPlan: PropTypes.object,
  locals: PropTypes.object,
  api: PropTypes.func,
};
